import React, { useState, useEffect } from 'react';
import {
  Box,
  CssBaseline,
  ThemeProvider,
  Typography,
  createTheme,
  IconButton,
  Fade,
  Paper,
  Stack,
  Button,
  useMediaQuery,
  alpha,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InsightsIcon from '@mui/icons-material/Insights';
import SettingsIcon from '@mui/icons-material/Settings';
import MessageIcon from '@mui/icons-material/Message';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CallIcon from '@mui/icons-material/Call';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HomeIcon from '@mui/icons-material/Home';
import CampaignIcon from '@mui/icons-material/Campaign';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';
import CombinedScheduleForm from './components/CombinedScheduleForm';
import ScheduledCallsList from './components/ScheduledCallsList';
import CallAnalytics from './components/CallAnalytics';
import Login from './components/Login';
import { authService } from './services/auth';
import ReminderSettings from './components/ReminderSettings';
import { reminderService } from './services/reminderService';
import ScheduledMessagesList from './components/ScheduledMessagesList';
import Campaigns from './components/Campaigns';
import CreateCampaign from './components/CreateCampaign';
import CampaignDetails from './components/CampaignDetails';
import { fetchPhoneChannels } from './components/PhoneNumberSelect';
import CallerIdSetup from './components/CallerIdSetup';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1e293b',
    },
    secondary: {
      main: '#ffffff',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#000000',
      secondary: alpha('#000000', 0.7),
    },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.7,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      letterSpacing: '-0.01em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 30,
          padding: '10px 24px',
          fontSize: '0.95rem',
        },
        contained: {
          backgroundColor: '#1e293b',
          color: '#fff',
          '&:hover': {
            backgroundColor: alpha('#1e293b', 0.9),
          },
        },
        outlined: {
          borderColor: '#1e293b',
          color: '#1e293b',
          '&:hover': {
            backgroundColor: alpha('#1e293b', 0.04),
            borderColor: '#1e293b',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: '1px solid',
          borderColor: alpha('#000', 0.08),
        },
      },
    },
  },
});

function App() {
  const [view, setView] = useState<'dashboard' | 'schedule' | 'list' | 'reminders' | 'messages' | 'campaigns' | 'create-campaign' | 'edit-campaign' | 'campaign-details' | 'caller-id'>(() => {
    // Initialize view from URL path
    const path = window.location.pathname.substring(1);
    const pathParts = path.split('/');
    const basePath = pathParts[0];

    if (['dashboard', 'schedule', 'list', 'reminders', 'messages', 'campaigns', 'create-campaign', 'edit-campaign', 'caller-id'].includes(basePath)) {
      return basePath as 'dashboard' | 'schedule' | 'list' | 'reminders' | 'messages' | 'campaigns' | 'create-campaign' | 'edit-campaign' | 'campaign-details' | 'caller-id';
    }
    
    // Handle campaign-details with ID in path
    if (basePath === 'campaign-details') {
      return 'campaign-details';
    }
    
    // Map old analytics path to dashboard
    if (basePath === 'analytics') {
      return 'dashboard';
    }
    return 'campaigns'; // Default to campaigns view
  });
  
  const [editingCampaignId, setEditingCampaignId] = useState<string | null>(null);
  const [viewingCampaignId, setViewingCampaignId] = useState<string | null>(() => {
    // Initialize campaign ID from URL path
    const path = window.location.pathname.substring(1);
    const pathParts = path.split('/');
    if (pathParts[0] === 'campaign-details' && pathParts[1]) {
      return pathParts[1];
    }
    return null;
  });

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasReminderAgent, setHasReminderAgent] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const checkReminderAgent = async () => {
      try {
        const response = await reminderService.getReminderSettings();
        setHasReminderAgent(!!response.results.reminder_agent);
        // If we're on the reminders view but there's no agent, redirect to campaigns
        if (view === 'reminders' && !response.results.reminder_agent) {
          setView('campaigns');
        }
      } catch (err) {
        console.error('Failed to check reminder agent:', err);
        setHasReminderAgent(false);
      }
    };

    if (isAuthenticated) {
      checkReminderAgent();
    }
  }, [isAuthenticated]);

  // Preload phone channels when navigating to pages that use PhoneNumberSelect
  useEffect(() => {
    if (isAuthenticated && (view === 'campaigns' || view === 'schedule' || view === 'list' || view === 'reminders' || view === 'create-campaign' || view === 'edit-campaign' || view === 'caller-id')) {
      // Preload phone channels in the background
      fetchPhoneChannels().catch((err) => {
        console.error('Failed to preload phone channels:', err);
      });
    }
  }, [isAuthenticated, view]);

      // Handle browser navigation
    useEffect(() => {
      const handleLocationChange = () => {
        const path = window.location.pathname.substring(1);
        const pathParts = path.split('/');
        const basePath = pathParts[0];
        
        if (['dashboard', 'schedule', 'list', 'reminders', 'messages', 'campaigns', 'create-campaign', 'edit-campaign', 'caller-id'].includes(basePath)) {
          setView(basePath as 'dashboard' | 'schedule' | 'list' | 'reminders' | 'messages' | 'campaigns' | 'create-campaign' | 'edit-campaign' | 'campaign-details' | 'caller-id');
        } else if (basePath === 'campaign-details') {
          setView('campaign-details');
          // Extract campaign ID from URL
          if (pathParts[1]) {
            setViewingCampaignId(pathParts[1]);
          }
        } else if (basePath === 'analytics') {
          // Map old analytics path to dashboard
          setView('dashboard');
        } else {
          setView('campaigns');
        }
      };

    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

      // Update URL when view changes
    useEffect(() => {
      let newPath = view === 'campaigns' ? '/' : `/${view}`;
      
      // Include campaign ID in URL for campaign-details view
      if (view === 'campaign-details' && viewingCampaignId) {
        newPath = `/campaign-details/${viewingCampaignId}`;
      }
      
      if (window.location.pathname !== newPath) {
        window.history.pushState({}, '', newPath);
      }
    }, [view, viewingCampaignId]);

  useEffect(() => {
    // Check URL parameters
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    const urlRefreshToken = params.get('refreshToken');
    const sourceUrl = params.get('source');

    // Update source URL in localStorage if provided
    if (sourceUrl) {
      localStorage.setItem('source_url', sourceUrl);
      // Clean up source from URL
      params.delete('source');
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    }

    // Check for authentication status (this will handle both URL tokens)
    const isAuth = authService.isAuthenticated();
    setIsAuthenticated(isAuth);
    
    // If not authenticated and no tokens in URL, redirect to login
    if (!isAuth && !urlToken && !urlRefreshToken) {
      console.log('No valid authentication found');
    }
  }, []);

  const handleBackToYobi = () => {
    const sourceUrl = localStorage.getItem('source_url') || 'https://app.yib.io';
    window.location.href = sourceUrl;
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
  };

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
          }}
        >
          <Login onLoginSuccess={handleLoginSuccess} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <CssBaseline />
        {!isAuthenticated ? (
          <Login onLoginSuccess={handleLoginSuccess} />
        ) : (
          <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            {/* Top Bar */}
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: 60,
                bgcolor: 'white',
                borderBottom: '1px solid',
                borderColor: alpha('#000', 0.08),
                zIndex: 1200,
                display: 'flex',
                alignItems: 'center',
                px: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box 
                  component="img" 
                  src="/yobi-favicon.svg" 
                  alt="Yobi Logo" 
                  sx={{ 
                    height: 32, 
                    width: 'auto' 
                  }} 
                />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  Outbound
                </Typography>
              </Box>
            </Box>
            
            {/* Navigation Bar */}
            <Drawer
              variant="permanent"
              open={true}
              sx={{
                width: 250,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                  width: 250,
                  boxSizing: 'border-box',
                  top: 60, // Position below the top bar
                  height: 'calc(100vh - 60px)', // Adjust height for top bar
                  // Hide scrollbar but allow scrolling
                  scrollbarWidth: 'none', // Firefox
                  '&::-webkit-scrollbar': {
                    display: 'none', // Chrome, Safari, Edge
                  },
                  msOverflowStyle: 'none', // Internet Explorer 10+
                },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                <List sx={{ flexGrow: 1 }}>
                  <ListItem>
                    <ListItemButton
                      onClick={() => setView('campaigns')}
                      selected={view === 'campaigns'}
                      sx={{
                        backgroundColor: alpha('#3b82f6', 0.06),
                        '&:hover': {
                          backgroundColor: alpha('#3b82f6', 0.12),
                        },
                        '&.Mui-selected': {
                          backgroundColor: alpha('#3b82f6', 0.15),
                          '&:hover': {
                            backgroundColor: alpha('#3b82f6', 0.2),
                          },
                        },
                      }}
                    >
                      <ListItemIcon>
                        <CampaignIcon sx={{ color: view === 'campaigns' ? '#3b82f6' : '#3b82f6' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Campaigns" 
                        primaryTypographyProps={{
                          sx: {
                            color: view === 'campaigns' ? '#3b82f6' : '#3b82f6',
                            fontWeight: view === 'campaigns' ? 600 : 500,
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                  <ListItem>
                    <ListItemButton 
                      onClick={() => setView('dashboard')}
                      selected={view === 'dashboard'}
                      sx={{
                        '&.Mui-selected': {
                          backgroundColor: alpha('#1e293b', 0.08),
                          '&:hover': {
                            backgroundColor: alpha('#1e293b', 0.12),
                          },
                        },
                      }}
                    >
                      <ListItemIcon>
                        <DashboardIcon sx={{ color: view === 'dashboard' ? '#1e293b' : 'inherit' }} />
                      </ListItemIcon>
                      <ListItemText primary="Dashboard" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem>
                    <ListItemButton 
                      onClick={() => setView('schedule')}
                      selected={view === 'schedule'}
                      sx={{
                        '&.Mui-selected': {
                          backgroundColor: alpha('#1e293b', 0.08),
                          '&:hover': {
                            backgroundColor: alpha('#1e293b', 0.12),
                          },
                        },
                      }}
                    >
                      <ListItemIcon>
                        <ScheduleIcon sx={{ color: view === 'schedule' ? '#1e293b' : 'inherit' }} />
                      </ListItemIcon>
                      <ListItemText primary="Schedule Calls" />
                    </ListItemButton>
                  </ListItem>

                  <ListItem>
                    <ListItemButton
                      onClick={() => setView('list')}
                      selected={view === 'list'}
                      sx={{
                        '&.Mui-selected': {
                          backgroundColor: alpha('#1e293b', 0.08),
                          '&:hover': {
                            backgroundColor: alpha('#1e293b', 0.12),
                          },
                        },
                      }}
                    >
                      <ListItemIcon>
                        <CallIcon sx={{ color: view === 'list' ? '#1e293b' : 'inherit' }} />
                      </ListItemIcon>
                      <ListItemText primary="View Calls" />
                    </ListItemButton>
                  </ListItem>

                  <ListItem>
                    <ListItemButton
                      onClick={() => setView('messages')}
                      selected={view === 'messages'}
                      sx={{
                        '&.Mui-selected': {
                          backgroundColor: alpha('#1e293b', 0.08),
                          '&:hover': {
                            backgroundColor: alpha('#1e293b', 0.12),
                          },
                        },
                      }}
                    >
                      <ListItemIcon>
                        <MessageIcon sx={{ color: view === 'messages' ? '#1e293b' : 'inherit' }} />
                      </ListItemIcon>
                      <ListItemText primary="View Messages" />
                    </ListItemButton>
                  </ListItem>
                  {hasReminderAgent && (
                    <ListItem>
                      <ListItemButton
                        onClick={() => setView('reminders')}
                        selected={view === 'reminders'}
                        sx={{
                          '&.Mui-selected': {
                            backgroundColor: alpha('#1e293b', 0.08),
                            '&:hover': {
                              backgroundColor: alpha('#1e293b', 0.12),
                            },
                          },
                        }}
                      >
                        <ListItemIcon>
                          <NotificationsIcon sx={{ color: view === 'reminders' ? '#1e293b' : 'inherit' }} />
                        </ListItemIcon>
                        <ListItemText primary="Reminders" />
                      </ListItemButton>
                    </ListItem>
                  )}
                  <ListItem>
                    <ListItemButton
                      onClick={() => setView('caller-id')}
                      selected={view === 'caller-id'}
                      sx={{
                        '&.Mui-selected': {
                          backgroundColor: alpha('#1e293b', 0.08),
                          '&:hover': {
                            backgroundColor: alpha('#1e293b', 0.12),
                          },
                        },
                      }}
                    >
                      <ListItemIcon>
                        <PhoneForwardedIcon sx={{ color: view === 'caller-id' ? '#1e293b' : 'inherit' }} />
                      </ListItemIcon>
                      <ListItemText primary="Caller ID" />
                    </ListItemButton>
                  </ListItem>
                </List>
                
                <Divider />
                
                <List>
                  <ListItem>
                    <ListItemButton onClick={handleBackToYobi}>
                      <ListItemIcon>
                        <ArrowBackIcon />
                      </ListItemIcon>
                      <ListItemText primary="Return Home" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem>
                    <ListItemButton onClick={handleLogout}>
                      <ListItemIcon>
                        <LogoutIcon />
                      </ListItemIcon>
                      <ListItemText primary="Logout" />
                    </ListItemButton>
                  </ListItem>
                </List>
              </Box>
            </Drawer>

            {/* Main Content */}
            <Box sx={{ ml: '250px', mt: '60px', flexGrow: 1 }}>
              <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
                <Fade in={true} timeout={450}>
                  <Box>
                    {view === 'dashboard' && (
                      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="h2" gutterBottom>Dashboard</Typography>
                        <CallAnalytics />
                      </Paper>
                    )}
                    {view === 'schedule' && (
                      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="h2" gutterBottom>Schedule Calls</Typography>
                        <CombinedScheduleForm onSuccess={() => {}} />
                      </Paper>
                    )}
                    {view === 'list' && (
                      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="h2" gutterBottom>Scheduled Calls</Typography>
                        <ScheduledCallsList />
                      </Paper>
                    )}
                    {view === 'campaigns' && (
                      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                        <Campaigns
                          showTitle={true}
                          onCreateCampaign={() => setView('create-campaign')}
                          onEditCampaign={(campaignId) => {
                            setEditingCampaignId(campaignId);
                            setView('edit-campaign');
                          }}
                          onViewCampaign={(campaignId) => {
                            setViewingCampaignId(campaignId);
                            setView('campaign-details');
                          }}
                        />
                      </Paper>
                    )}
                    {view === 'create-campaign' && (
                      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                        <CreateCampaign
                          onBack={() => setView('campaigns')}
                          onSuccess={() => setView('campaigns')}
                        />
                      </Paper>
                    )}
                    {view === 'edit-campaign' && (
                      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                        <CreateCampaign
                          campaignId={editingCampaignId}
                          onBack={() => {
                            setEditingCampaignId(null);
                            setView('campaigns');
                          }}
                          onSuccess={() => {
                            setEditingCampaignId(null);
                            setView('campaigns');
                          }}
                        />
                      </Paper>
                    )}
                    {view === 'campaign-details' && (
                      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                        <CampaignDetails
                          campaignId={viewingCampaignId}
                          onBack={() => {
                            setViewingCampaignId(null);
                            setView('campaigns');
                          }}
                        />
                      </Paper>
                    )}
                    {view === 'messages' && (
                      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="h2" gutterBottom>Scheduled Messages</Typography>
                        <ScheduledMessagesList />
                      </Paper>
                    )}
                    {view === 'reminders' && (
                      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="h2" gutterBottom>Reminder Settings</Typography>
                        <ReminderSettings />
                      </Paper>
                    )}
                    {view === 'caller-id' && (
                      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                        <CallerIdSetup />
                      </Paper>
                    )}
                  </Box>
                </Fade>
              </Container>
            </Box>
          </Box>
        )}
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App; 